import React, { useContext, useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { formatFilterValues } from '@/pages/dashboard/utils/utils';
import './types';
import { fillRealtimeEventData } from '../../_config/common';
import { getAccumulation, getAccumulationListFromFilterValue } from '@/pages/dashboard/utils/filter';
import { filterFlatListsByNameGroup, filterResponseData, filterTopListsByConfig, formatByDimensionCard, getUaDimensionShareData, IFilterDataConfig } from '../_utils';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';

interface Entry {
  time: string;
  value: string;
}

interface Country {
  name: string;
  list: Entry[];
}

const formatDataMap: {
  [key: string]: (response: any, filterDataObj: IFilterDataConfig, listField?: string) => any;
} = {
  'analysis_online': filterFlatListsByNameGroup,
  'country_new_active_online': filterTopListsByConfig
};

export default function RealtimeUserService(props: any) {
  // filterDataObj 返回数据间隔多少过滤数据
  const { style, className, id, type, config, children, data: propsData, ...resetProps } = props || {};
  const { api, dataKey, isDark = false, refreshKey = '', interval, extraParams = {}, filterDataObj, formatParams, formatData, isByDimensionCard } = config || {};
  // const [dataBy, setDataBy] = useState('country'); // 后续下拉组件封装好了需要传入dataBy
  const { filterVal } = useContext(FilterContext);

  const { gameCode } = useSelector((state: any) => ({
    gameCode: state?.dashboard?.currentGame?.game_code,
  }), shallowEqual);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const data = useMemo(() => ({
    gameCode,
    isDark,
    ...(propsData || {})
  }), [gameCode, propsData, isDark]);

  return (
    <div className={className} style={style} id={id}>
      <Service
        api={api}
        data={data}
        dataKey={dataKey}
        refreshKey={refreshKey}
        interval={interval}
        beforeRequest={(data) => {
          const { filterVal, gameCode, dataBy, is_key_country, trend_type, hour } = data;

          const accumulationList: string[] = getAccumulationListFromFilterValue(filterVal); // 从筛选器调取accumulation, 例子: by_system, by_region
          const accumulation: Record<string, boolean> = getAccumulation(accumulationList, dataBy); // 通过dataBy找出目前选择的维度

          const { contrastDaily, singleDaily, ...filters } = filterVal || {};

          // 传参兜底筛选器参数
          let params: IApiParamsBase | IApiParamsWithKey = {
            game_code: gameCode,
            filters: formatFilterValues(filters),
            date: singleDaily,
            contrast_day: contrastDaily,
            ...(extraParams || {}),
          };
          // 用户
          if (dataKey === 'date_event_list') {
            delete params.contrast_day;
            delete params.date;
            delete params.filters.diff;
            delete params.filters.timeType;
            params = {
              ...params,
              start_time: singleDaily,
              end_time: singleDaily,
              period: 'hours',
            };
          }
          // 国家/区域、区服
          if (dataKey === 'country_new_active_online' || dataKey === 'country_d237' || 'revenue_units') {
            params = {
              ...params,
              accumulation,
            };
          }
          // 异动分析
          if (dataKey === 'analysis_online' || dataKey === 'analysis_d237') {
            params = {
              ...params,
              is_key_country: is_key_country,
              trend_type: trend_type,
              ...(hour !== '23:59' && { hour: hour }),
            };
          }
          if (extraParams?.accumulation) {
            params = {
              ...params,
              accumulation: extraParams?.accumulation,
            };
          }
          // 格式化参数
          if (formatParams) {
            if(typeof formatParams === 'function') {
              return formatParams?.(params);
            }
            if(typeof formatParams === 'string') {
              return (stringToFunction?.(formatParams))?.(params);
            }
          }
          return params;
        }}
        trackData={['gameCode', 'isDark', 'hour', 'trend_type', 'is_key_country', 'dataBy']}
        formatData={(response) => {
          if (isByDimensionCard) {
            return formatByDimensionCard(response, dataKey);
          }
          if (dataKey === 'date_event_list') {

            const addItem = {
              date: filterVal?.singleDaily || '',
              time: "last_moment",
              events: 0,
              event_list: []
            };

            const apiData: any = response?.list?.length ? [...(response?.list || []), addItem] : [];
            const newAndActive = fillRealtimeEventData(apiData, 5, 288);
            const online = fillRealtimeEventData(apiData, 5, 287);

            return {
              new_users: newAndActive,
              active_users: newAndActive,
              online_users: online
            };
          }

          // ua分维度接口处理share数据
          if (api === '/api/v1/dashboard/pc/realtime/ua/dimension/trend') {
            getUaDimensionShareData(response, extraParams?.metrics?.[0]);
          }

          if (dataKey === 'country_new_active_online' || dataKey === 'revenue_units' || dataKey === 'country_avg_playtime_matches') {
            // 占比计算
            const shareDataObj: any = {};
            Object.keys(response).forEach((key) => {
              const shareData: { [time: string]: any; } = {};
              if (key?.slice(-4) === "_top") { // top数据
                // 处理share汇总值
                response?.[key]?.forEach((country: Country) => {
                  country?.list?.forEach((each: Entry) => {
                    const { time, value } = each;
                    if (!shareData?.[time]) {
                      shareData[time] = parseFloat(value) || 0;
                    } else {
                      shareData[time] += parseFloat(value) || 0;
                    }
                  });
                });
                shareDataObj[key?.slice(0, key?.length - 4)] = shareData;
              }
            });
            Object.keys(response).forEach((key) => {
              if (key?.slice(-4) === "_top") { // top数据
                const shareData = shareDataObj?.[key?.slice(0, key?.length - 4)];
                // 处理share值
                response?.[key]?.forEach((val: Country) => {
                  const { list } = val;
                  val.list = list?.map((each) => ({
                    ...each,
                    share: parseFloat(each?.value) / parseFloat(shareData?.[each?.time]) || '-'
                  }));
                });
              }
              if (key?.slice(-5) === "_list") { // list数据
                const shareData = shareDataObj?.[key?.slice(0, key?.length - 5)];
                // 处理share值
                response[key] = response?.[key]?.map((val: Entry) => ({
                  ...val,
                  share: parseFloat(val.value) / parseFloat(shareData?.[val?.time]) || '-'
                }));
              }
            });
          }
          // 应用数据过滤
          if (filterDataObj && Object.keys(filterDataObj).length > 0) {
            return filterResponseData(response, filterDataObj);
          }

          // 应用数据过滤
          if (formatData) {
            if(typeof formatData === 'function') {
              return formatData?.(response, formatDataMap?.[dataKey]);
            }
            if(typeof formatData === 'string') {
              return (stringToFunction?.(formatData))?.(response, formatDataMap?.[dataKey]);
            }
            return response;
          }
          return response;
        }}
      >
        {React.Children.map(children, (child: any) => React.cloneElement(
          child,
          {
            filterVal,
            dataKey,
            ...resetProps,
            state: {}
          }
        ))}
      </Service>
    </div>
  );
};